import { StorageNotificationTemplate } from "@dashboard-v3/api";
import { NOTIFICATION_LIMITS } from "@dashboard-v3/shared";

export type { StorageNotificationTemplate };

export type StorageNotificationTemplateType =
  | "ATTACHMENT_PDF"
  | "EMBEDDED_LINKS"
  | "SECURE_EMAIL"
  | "PASSWORD_NOTIFICATION"
  | "REJECT";

export function getActiveTypes(
  template: StorageNotificationTemplate
): StorageNotificationTemplateType[] {
  const result: StorageNotificationTemplateType[] = [];
  if (template.attachmentPdf) result.push("ATTACHMENT_PDF");
  if (template.embeddedLinks) result.push("EMBEDDED_LINKS");
  if (template.secureEmail) result.push("SECURE_EMAIL");
  if (template.passwordNotification) result.push("PASSWORD_NOTIFICATION");
  if (template.reject) result.push("REJECT");
  return result;
}

export type FieldKind = "short" | "layout";
export type FieldState = "default" | "custom" | "off";

export type FieldDef = {
  key: string;
  kind: FieldKind;
  maxLength: number;
  sectionKey?: string;
};

export type ToggleDef = {
  key: string;
};

export const NAME_MAX_LENGTH = 100;

export type TypeConfig = {
  type: StorageNotificationTemplateType;
  formKey: string;
  fields: FieldDef[];
  toggles?: ToggleDef[];
};

export const TYPE_CONFIGS: TypeConfig[] = [
  {
    type: "ATTACHMENT_PDF",
    formKey: "attachmentPdf",
    fields: [
      { key: "title", kind: "short", maxLength: NOTIFICATION_LIMITS.TITLE },
      { key: "topMessage", kind: "short", maxLength: NOTIFICATION_LIMITS.MESSAGE },
      {
        key: "passwordNotice",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showPasswordNotice",
      },
      {
        key: "expirationNotice",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showExpirationNotice",
      },
      {
        key: "openAccessMessage",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showOpenAccess",
      },
      {
        key: "restrictedAccessMessage",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showRestrictedAccess",
      },
      {
        key: "subscriptionLinkText",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.CTA_LABEL,
        sectionKey: "showSubscriptionLink",
      },
      {
        key: "companyAccessMessage",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showCompanyAccess",
      },
      { key: "passwordAccessMessage", kind: "short", maxLength: NOTIFICATION_LIMITS.MESSAGE },
      {
        key: "downloadWarning",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showDownloadWarning",
      },
      { key: "downloadLabel", kind: "short", maxLength: NOTIFICATION_LIMITS.CTA_LABEL },
      { key: "viewLabel", kind: "short", maxLength: NOTIFICATION_LIMITS.CTA_LABEL },
      { key: "folderOpenLabel", kind: "short", maxLength: NOTIFICATION_LIMITS.CTA_LABEL },
      { key: "headerHtml", kind: "layout", maxLength: NOTIFICATION_LIMITS.HTML_LAYOUT },
      { key: "footerHtml", kind: "layout", maxLength: NOTIFICATION_LIMITS.HTML_LAYOUT },
    ],
  },
  {
    type: "EMBEDDED_LINKS",
    formKey: "embeddedLinks",
    fields: [
      { key: "topMessage", kind: "short", maxLength: NOTIFICATION_LIMITS.MESSAGE },
      {
        key: "passwordNotice",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showPasswordNotice",
      },
      {
        key: "expirationNotice",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showExpirationNotice",
      },
      {
        key: "openAccessMessage",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showOpenAccess",
      },
      {
        key: "restrictedAccessMessage",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showRestrictedAccess",
      },
      {
        key: "subscriptionLinkText",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.CTA_LABEL,
        sectionKey: "showSubscriptionLink",
      },
      {
        key: "companyAccessMessage",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showCompanyAccess",
      },
      { key: "passwordAccessMessage", kind: "short", maxLength: NOTIFICATION_LIMITS.MESSAGE },
      { key: "downloadLabel", kind: "short", maxLength: NOTIFICATION_LIMITS.CTA_LABEL },
      { key: "viewLabel", kind: "short", maxLength: NOTIFICATION_LIMITS.CTA_LABEL },
      { key: "folderOpenLabel", kind: "short", maxLength: NOTIFICATION_LIMITS.CTA_LABEL },
      { key: "originalMailLabel", kind: "short", maxLength: NOTIFICATION_LIMITS.CTA_LABEL },
      { key: "footer", kind: "layout", maxLength: NOTIFICATION_LIMITS.HTML_LAYOUT },
    ],
    toggles: [{ key: "showPlainDownloadLinks" }],
  },
  {
    type: "SECURE_EMAIL",
    formKey: "secureEmail",
    fields: [
      { key: "title", kind: "short", maxLength: NOTIFICATION_LIMITS.TITLE },
      { key: "topMessage", kind: "short", maxLength: NOTIFICATION_LIMITS.MESSAGE },
      {
        key: "passwordNotice",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showPasswordNotice",
      },
      {
        key: "expirationNotice",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showExpirationNotice",
      },
      { key: "bodyMessage", kind: "short", maxLength: NOTIFICATION_LIMITS.MESSAGE },
      { key: "bodyLink", kind: "short", maxLength: NOTIFICATION_LIMITS.CTA_LABEL },
      {
        key: "openAccessMessage",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showOpenAccess",
      },
      {
        key: "restrictedAccessMessage",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showRestrictedAccess",
      },
      {
        key: "subscriptionLinkText",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.CTA_LABEL,
        sectionKey: "showSubscriptionLink",
      },
      {
        key: "companyAccessMessage",
        kind: "short",
        maxLength: NOTIFICATION_LIMITS.MESSAGE,
        sectionKey: "showCompanyAccess",
      },
      { key: "passwordAccessMessage", kind: "short", maxLength: NOTIFICATION_LIMITS.MESSAGE },
    ],
  },
  {
    type: "PASSWORD_NOTIFICATION",
    formKey: "passwordNotification",
    fields: [
      { key: "recipientMessage", kind: "short", maxLength: NOTIFICATION_LIMITS.MESSAGE },
      { key: "senderMessage", kind: "short", maxLength: NOTIFICATION_LIMITS.MESSAGE },
      { key: "senderMessageAll", kind: "short", maxLength: NOTIFICATION_LIMITS.MESSAGE },
      { key: "subjectTag", kind: "short", maxLength: NOTIFICATION_LIMITS.SUBJECT_TAG },
    ],
  },
  {
    type: "REJECT",
    formKey: "reject",
    fields: [
      { key: "rejectMessage", kind: "short", maxLength: NOTIFICATION_LIMITS.MESSAGE },
      { key: "subjectTag", kind: "short", maxLength: NOTIFICATION_LIMITS.SUBJECT_TAG },
    ],
  },
];
